import React, {
    FC,
    useEffect,
    useState,
} from "react"
import {
    Alert,
    Badge,
    Button,
    ButtonGroup,
    Card,
    Col,
    Container,
    OverlayTrigger,
    Row,
    Tooltip,
} from "react-bootstrap"
import axios from "axios"
import moment from "moment"
import BootstrapTable from "react-bootstrap-table-next"
import filterFactory, {
    textFilter,
} from "react-bootstrap-table2-filter"
import {useNavigate} from "react-router-dom"

import {useAuth} from "../../context/AuthProvider"
import {useTranslation} from "react-i18next"
import {
    ManagedExam,
    ManagedExamTenant,
} from "./participant.types"

type ExamManagementComponentProps =
    Record<string, unknown>

const ExamManagementComponent: FC<
    ExamManagementComponentProps
> = () => {
    const auth = useAuth()
    const nav = useNavigate()
    const {t, i18n} = useTranslation("examManagement")

    const [exams, setExams] =
        useState<ManagedExam[]>([])

    const [loading, setLoading] =
        useState<boolean>(true)

    const [loadError, setLoadError] =
        useState<boolean>(false)

    const mayManageExecutingTenants =
        auth.hasAnyPermission([
            "examOfficeEmployee",
            "examOfficeAssistant",
        ])

    useEffect(() => {
        axios
            .get(
                "/api/exam/list",
                auth.axiosAuthHeader()
            )
            .then(response => {
                setExams(
                    Array.isArray(response.data)
                        ? response.data
                        : []
                )

                setLoadError(false)
            })
            .catch(error => {
                console.error(
                    "[Exam management] Failed to load exams",
                    error
                )

                setExams([])
                setLoadError(true)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [])

    const caretDefinition = (
        order: string | undefined
    ) => {
        if (!order) {
            return (
                <i className="bi bi-arrow-down-up ms-1"/>
            )
        }

        return order === "asc"
            ? <i className="bi bi-sort-up ms-1"/>
            : <i className="bi bi-sort-down ms-1"/>
    }

    const formatDate = (
        value: string | undefined
    ): string => {
        if (!value) {
            return "–"
        }

        return moment(value).format(
            "DD.MM.YYYY HH:mm"
        )
    }

    const getTenantLabel = (
        tenant: ManagedExamTenant
    ): string => {
        return (
            (i18n.language === "en" ? tenant.fullName?.en : tenant.fullName?.de) ||
            (i18n.language === "en" ? tenant.name?.en : tenant.name?.de) ||
            tenant.describer
        )
    }

    const columns = [
        {
            dataField: "name_de",
            text: t("overview.columns.exam"),
            sort: true,
            filter: textFilter({
                placeholder: " ",
            }),
            sortCaret: caretDefinition,
        },
        {
            dataField: "module",
            text: t("overview.columns.module"),
            sort: true,
            filter: textFilter({
                placeholder: " ",
            }),
            formatter: (
                cell: string | undefined
            ) => {
                return cell || "–"
            },
            sortCaret: caretDefinition,
        },
        {
            dataField: "executingTenants",
            text: t("overview.columns.executingTenants"),
            sort: true,
            filter: textFilter({
                placeholder: " ",
            }),
            formatter: (
                cell: ManagedExamTenant[] | undefined
            ) => {
                if (
                    !Array.isArray(cell) ||
                    cell.length === 0
                ) {
                    return "–"
                }

                return (
                    <div className="d-flex flex-column gap-1">
                        {cell.map(tenant => (
                            <span key={tenant._id}>
                                {getTenantLabel(
                                    tenant
                                )}
                            </span>
                        ))}
                    </div>
                )
            },
            filterValue: (
                cell: ManagedExamTenant[] | undefined
            ) => {
                if (!Array.isArray(cell)) {
                    return ""
                }

                return cell
                    .map(getTenantLabel)
                    .join(" ")
            },
            sortValue: (
                cell: ManagedExamTenant[] | undefined
            ) => {
                if (!Array.isArray(cell)) {
                    return ""
                }

                return cell
                    .map(getTenantLabel)
                    .sort((a, b) =>
                        a.localeCompare(
                            b,
                            "de"
                        )
                    )
                    .join(", ")
            },
            sortCaret: caretDefinition,
        },
        {
            dataField: "openBookingAt",
            text: t("overview.columns.bookingPeriod"),
            sort: true,
            filter: textFilter({
                placeholder: " ",
            }),
            formatter: (
                _cell: string | undefined,
                row: ManagedExam
            ) => {
                return (
                    <>
                        {formatDate(
                            row.openBookingAt
                        )}
                        <br/>
                        {t("until")}{" "}
                        {formatDate(
                            row.closeBookingAt
                        )}
                    </>
                )
            },
            filterValue: (
                _cell: string | undefined,
                row: ManagedExam
            ) => {
                return [
                    formatDate(
                        row.openBookingAt
                    ),
                    formatDate(
                        row.closeBookingAt
                    ),
                ].join(" ")
            },
            sortValue: (
                _cell: string | undefined,
                row: ManagedExam
            ) => {
                return row.openBookingAt
            },
            sortCaret: caretDefinition,
        },
        {
            dataField: "openExaminationRange",
            text: t("overview.columns.examPeriod"),
            sort: true,
            filter: textFilter({
                placeholder: " ",
            }),
            formatter: (
                _cell: string | undefined,
                row: ManagedExam
            ) => {
                return (
                    <>
                        {formatDate(
                            row.openExaminationRange
                        )}
                        <br/>
                        {t("until")}{" "}
                        {formatDate(
                            row.closeExaminationRange
                        )}
                    </>
                )
            },
            filterValue: (
                _cell: string | undefined,
                row: ManagedExam
            ) => {
                return [
                    formatDate(
                        row.openExaminationRange
                    ),
                    formatDate(
                        row.closeExaminationRange
                    ),
                ].join(" ")
            },
            sortValue: (
                _cell: string | undefined,
                row: ManagedExam
            ) => {
                return row.openExaminationRange
            },
            sortCaret: caretDefinition,
        },
        {
            dataField: "status",
            text: t("overview.columns.status"),
            formatter: (
                _cell: unknown,
                row: ManagedExam
            ) => {
                const active =
                    moment().isBetween(
                        moment(
                            row.openBookingAt
                        ),
                        moment(
                            row.closeExaminationRange
                        ),
                        undefined,
                        "[]"
                    )

                return (
                    <div className="d-flex flex-wrap gap-1">
                        {active && (
                            <Badge bg="success">
                                {t("overview.active")}
                            </Badge>
                        )}

                        {row.tucanIds?.length ? (
                            <Badge bg="info">
                                {t("overview.tucanSynced")}
                            </Badge>
                        ) : null}
                    </div>
                )
            },
            sort: false,
            filter: false,
        },
        {
            dataField: "actions",
            text: t("overview.columns.actions"),
            formatter: (
                _cell: unknown,
                row: ManagedExam
            ) => {
                return (
                    <ButtonGroup size="sm">
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip
                                    id={`participants-${row._id}`}
                                >
                                    {t("participants.title")}
                                </Tooltip>
                            }
                        >
                            <Button
                                onClick={() => {
                                    nav(
                                        `/exam-management/${row._id}/participants`
                                    )
                                }}
                            >
                                <i className="bi bi-people-fill"/>
                            </Button>
                        </OverlayTrigger>

                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip
                                    id={`booking-distribution-${row._id}`}
                                >
                                    {t("distribution.shortTitle")}
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    nav(
                                        `/bookingDistribution/${row._id}`
                                    )
                                }}
                            >
                                <i className="bi bi-bar-chart-steps"/>
                            </Button>
                        </OverlayTrigger>

                        {mayManageExecutingTenants && (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip
                                        id={`tenants-${row._id}`}
                                    >
                                        {t("executingTenants.manage")}
                                    </Tooltip>
                                }
                            >
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        nav(
                                            `/exam-management/${row._id}/tenants`
                                        )
                                    }}
                                >
                                    <i className="bi bi-buildings"/>
                                </Button>
                            </OverlayTrigger>
                        )}

                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip
                                    id={`access-${row._id}`}
                                >
                                    {t("access.effective")}
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    nav(
                                        `/exam-management/${row._id}/access`
                                    )
                                }}
                            >
                                <i className="bi bi-shield-lock"/>
                            </Button>
                        </OverlayTrigger>
                    </ButtonGroup>
                )
            },
            sort: false,
            filter: false,
        },
    ]

    return (
        <div className="component">
            <Container fluid>
                <Row>
                    <Col>
                        <Card>
                            <Card.Header>
                                <h3 className="mb-0">
                                    <i className="bi bi-clipboard-data me-2"/>
                                    {t("overview.title")}
                                </h3>
                            </Card.Header>

                            <Card.Body>
                                {loading && (
                                    <Alert
                                        className="text-center"
                                        variant="info"
                                    >
                                        {t("overview.loading")}
                                    </Alert>
                                )}

                                {!loading &&
                                loadError && (
                                    <Alert
                                        className="text-center"
                                        variant="danger"
                                    >
                                        {t("overview.loadError")}
                                    </Alert>
                                )}

                                {!loading &&
                                !loadError &&
                                exams.length === 0 && (
                                    <Alert
                                        className="text-center"
                                        variant="info"
                                    >
                                        {t("overview.empty")}
                                    </Alert>
                                )}

                                {!loading &&
                                !loadError &&
                                exams.length > 0 && (
                                    <BootstrapTable
                                        keyField="_id"
                                        data={exams}
                                        columns={columns}
                                        filter={filterFactory()}
                                        striped
                                        hover
                                        condensed
                                        defaultSorted={[
                                            {
                                                dataField:
                                                    "openExaminationRange",
                                                order:
                                                    "desc",
                                            },
                                        ]}
                                        noDataIndication={() => (
                                            <Alert
                                                className="text-center"
                                                variant="info"
                                            >
                                                {t("noResults")}
                                            </Alert>
                                        )}
                                    />
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default ExamManagementComponent
